import React from 'react';
import type { RegionHubStats, RegionHubTown } from '../../../types/region-hub';
import { getFinnishRegionLocative } from '../../../utils/i18n/finnish-region-locative';
import { FINLAND_REGIONS, FINLAND_MAP_VIEWBOX } from '../../../utils/map/finland-region-geometry';
import { hubIndexPath, buildEmployersInCityLink } from '../../../utils/seo/hub-index-links';
import { LOCALIZED_PATHS } from '../../../utils/constants/url-constants';
import { buildLocationSrpPath } from '../../../utils/location/build-location-srp-path';
import { findRoleForNormalisedTitle } from '../../../utils/role/find-role-for-title';

type Locale = 'en' | 'fi' | 'sv';

export interface RegionHeaderProps {
  regionHubData: RegionHubStats;
  locale: Locale;
  className?: string;
}

function formatCount(n: number, locale: Locale): string {
  // fi/sv both get the narrow no-break space thousands separator; 'fi-FI' groups with a plain
  // NBSP (U+00A0) in this runtime's ICU data, so swap it for the narrow variant (U+202F) by hand.
  if (locale === 'en') return n.toLocaleString('en-US');
  return n.toLocaleString('fi-FI').replace(/ /g, ' ');
}

function formatPct(pct: number | null, locale: Locale): string {
  if (pct === null) return '–'; // en dash placeholder, never an em dash
  return locale === 'en' ? `${pct}%` : `${formatCount(pct, locale)} %`;
}

function formatSalaryRange(min: number, max: number, locale: Locale): string {
  return `€${formatCount(Math.round(min), locale)}–${formatCount(Math.round(max), locale)}`;
}

/** Same in-code locale maps as RoleHeader/CategoryHeader: this header is part of the
 *  server-rendered SRP shell, which renders without an IntlProvider. */
const STRINGS: Record<Locale, {
  breadcrumbHome: string;
  breadcrumbLocations: string;
  backToFinland: string;
  statOpenNow: string;
  statNewInWeek: string;
  statNoFinnish: string;
  statFlex: string;
  statSalary: string;
  salaryFor: (city: string) => string;
  lede: (count: string, townCount: number, largestTown: string, sharePct: number) => string;
  mapHint: string;
  mapAttribution: string;
  tableRank: string;
  tableTown: string;
  tableShare: string;
  tableOpen: string;
  panelKicker: (region: string) => string;
  panelRankOf: (rank: number, total: number) => string;
  panelHeadlineLabel: string;
  panelShareLabel: string;
  panelRolesHeading: string;
  panelCta: (count: string, town: string) => string;
  topRolesHeading: (region: string) => string;
  neighboursHeading: string;
  linkAllRoles: string;
  linkSalary: string;
  linkEmployers: (place: string) => string;
  footnote: (minJobs: number) => string;
}> = {
  fi: {
    breadcrumbHome: 'Etusivu',
    breadcrumbLocations: 'Sijainnit',
    backToFinland: 'Koko Suomi',
    statOpenNow: 'Avoinna nyt',
    statNewInWeek: 'Uusia 7 päivässä',
    statNoFinnish: 'Ilman suomen vaatimusta',
    statFlex: 'Etä- tai hybridityö',
    statSalary: 'Mediaanipalkka',
    salaryFor: (city) => `Mediaanipalkka (${city})`,
    lede: (count, townCount, largestTown, sharePct) =>
      `${count} avointa työpaikkaa ${townCount} paikkakunnalla, kerätty suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun. ${largestTown} kattaa ${sharePct} % maakunnan paikoista.`,
    mapHint: 'Ympyrän koko kuvaa avoimia paikkoja. Napauta kuntaa.',
    mapAttribution: 'Aluerajat: Tilastokeskus (CC BY 4.0)',
    tableRank: '#',
    tableTown: 'Paikkakunta',
    tableShare: 'Suhteessa kärkeen',
    tableOpen: 'Avoinna',
    panelKicker: (region) => `${region} · kunta`,
    panelRankOf: (rank, total) => `${rank}. / ${total} maakunnassa`,
    panelHeadlineLabel: 'avointa työpaikkaa',
    panelShareLabel: 'Osuus maakunnan paikoista',
    panelRolesHeading: 'Ammatit paikkakunnalla',
    panelCta: (count, town) => `Kaikki ${count} työpaikkaa: ${town}`,
    topRolesHeading: (region) => `Eniten haetut ammatit ${region}`,
    neighboursHeading: 'Naapurimaakunnat',
    linkAllRoles: 'Ammatit',
    linkSalary: 'Palkat',
    linkEmployers: (place) => `Työnantajat ${place}`,
    footnote: (minJobs) =>
      `Luvut lasketaan suoraan hakuindeksistä ja päivittyvät päivittäin. Alle ${minJobs} ilmoituksen paikkakunnat näytetään ilman linkkiä.`,
  },
  sv: {
    breadcrumbHome: 'Hem',
    breadcrumbLocations: 'Platser',
    backToFinland: 'Hela Finland',
    statOpenNow: 'Lediga just nu',
    statNewInWeek: 'Nya senaste 7 dagarna',
    statNoFinnish: 'Utan finskakrav',
    statFlex: 'Distans- eller hybridarbete',
    statSalary: 'Medianlön',
    salaryFor: (city) => `Medianlön (${city})`,
    lede: (count, townCount, largestTown, sharePct) =>
      `${count} lediga jobb i ${townCount} orter, samlade direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning. ${largestTown} står för ${sharePct} % av landskapets platser.`,
    mapHint: 'Cirkelns storlek visar antalet lediga platser. Tryck på en ort.',
    mapAttribution: 'Regiongränser: Statistikcentralen (CC BY 4.0)',
    tableRank: '#',
    tableTown: 'Ort',
    tableShare: 'I förhållande till toppen',
    tableOpen: 'Lediga',
    panelKicker: (region) => `${region} · kommun`,
    panelRankOf: (rank, total) => `${rank}. / ${total} i landskapet`,
    panelHeadlineLabel: 'lediga jobb',
    panelShareLabel: 'Andel av landskapets platser',
    panelRolesHeading: 'Yrken på orten',
    panelCta: (count, town) => `Alla ${count} jobb: ${town}`,
    topRolesHeading: (region) => `Mest sökta yrken i ${region}`,
    neighboursHeading: 'Grannlandskap',
    linkAllRoles: 'Yrken',
    linkSalary: 'Löner',
    linkEmployers: (place) => `Arbetsgivare i ${place}`,
    footnote: (minJobs) =>
      `Siffrorna räknas direkt från sökindexet och uppdateras dagligen. Orter med färre än ${minJobs} annonser visas utan länk.`,
  },
  en: {
    breadcrumbHome: 'Home',
    breadcrumbLocations: 'Locations',
    backToFinland: 'All of Finland',
    statOpenNow: 'Open now',
    statNewInWeek: 'New in 7 days',
    statNoFinnish: 'No Finnish required',
    statFlex: 'Remote or hybrid',
    statSalary: 'Median pay',
    salaryFor: (city) => `Median pay (${city})`,
    lede: (count, townCount, largestTown, sharePct) =>
      `${count} open positions across ${townCount} towns, gathered straight from employers' own sites and Työmarkkinatori into one search. ${largestTown} accounts for ${sharePct}% of the region's jobs.`,
    mapHint: 'Circle size reflects open jobs. Tap a town.',
    mapAttribution: 'Region borders: Statistics Finland (CC BY 4.0)',
    tableRank: '#',
    tableTown: 'Town',
    tableShare: 'Relative to the top',
    tableOpen: 'Open',
    panelKicker: (region) => `${region} · town`,
    panelRankOf: (rank, total) => `${rank} of ${total} in the region`,
    panelHeadlineLabel: 'open positions',
    panelShareLabel: "Share of the region's jobs",
    panelRolesHeading: 'Roles in this town',
    panelCta: (count, town) => `All ${count} jobs: ${town}`,
    topRolesHeading: (region) => `Most searched roles in ${region}`,
    neighboursHeading: 'Neighbouring regions',
    linkAllRoles: 'Roles',
    linkSalary: 'Salaries',
    linkEmployers: (place) => `Employers in ${place}`,
    footnote: (minJobs) =>
      `Figures are computed straight from the search index and refresh daily. Towns with fewer than ${minJobs} listings show without a link.`,
  },
};

/** H1 for a maakunta (Finnish admin1 region) hub SRP ("Avoimet työpaikat Uusimaalla" /
 * "Jobs in Uusimaa" / "Lediga jobb i Nyland"). fi uses the region's locative case
 * (finnish-region-locative.ts), matching generate-srp-seo-meta.ts's H1 grammar for a bare
 * regionTags admin1 filter; falls back to the nominative name if a placeId has no curated
 * locative entry, same fail-open behavior generate-srp-seo-meta.ts uses.
 */
export function buildRegionHeaderH1(placeId: string, name: string, locale: Locale): string {
  if (locale === 'fi') {
    const locative = getFinnishRegionLocative(placeId) ?? name;
    return `Avoimet työpaikat ${locative}`;
  }
  if (locale === 'sv') return `Lediga jobb i ${name}`;
  return `Jobs in ${name}`;
}

const MIN_JOBS_FOOTNOTE = 5;
const TEAL_RAMP = ['#b6d2d1', '#84b1af', '#4f8583', '#2b4f4e'];
const ACTIVE_FILL = '#c47a52';
const MAP_PAD = 10;
const TOP_LABELLED_TOWNS = 9;

/**
 * Bounding box of every town with a map point, padded, in FINLAND_MAP_VIEWBOX's own coordinate
 * space. Falls back to the whole-Finland viewBox when no town has a plotted point (e.g. Åland,
 * which has no municipality points seeded), rather than crashing on an empty min/max.
 */
function regionMapViewBox(towns: RegionHubTown[]): string {
  const points = towns.map((t) => t.point).filter((p): p is [number, number] => p !== null);
  if (points.length === 0) return FINLAND_MAP_VIEWBOX;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs) - MAP_PAD;
  const minY = Math.min(...ys) - MAP_PAD;
  const maxX = Math.max(...xs) + MAP_PAD;
  const maxY = Math.max(...ys) + MAP_PAD;
  return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
}

/**
 * Static SEO header for `/jobs/region/{slug}` (fi maakunta, sv landskap) pages: breadcrumb, H1,
 * live count, five stat tiles, and a town explorer (zoomed choropleth + ranked list + detail
 * panel). Part of the server-rendered SRP shell (like RoleHeader/CategoryHeader), so it renders
 * without an IntlProvider: all copy comes from the in-code STRINGS map above, not react-intl.
 *
 * The town explorer's hover/click selection is plain useState: the default selection (the
 * region's largest town) is fully determined by `regionHubData.towns`, so the server-rendered
 * markup and the first client render are identical, no hydration-mismatch guard needed beyond
 * that determinism. Every town circle/row still carries a real `href` to its own page, so the
 * explorer degrades to a set of ordinary links with JS disabled; the onClick handler only
 * intercepts the click to update the panel in place instead of navigating away.
 *
 * Deviations from the approved mockup (Region.dc.html), noted here rather than silently:
 *  - No role-filter chip row above the map (the "Kaikki / Sairaanhoitaja / Python / ..." toggle):
 *    it needs a per-role-per-town breakdown this page's data layer doesn't compute, and isn't
 *    listed in the DELIVERABLES data contract. The static top-roles chips below the explorer
 *    (from `regionHubData.topRoles`) cover the "what roles are open here" need instead.
 *  - No "Uusimmat työpaikat {region}" job-card grid: the task brief says the normal SRP results
 *    continue, unmodified, right below this header: that grid is exactly those results.
 *  - The map's zoom viewBox is the padded bounding box of the region's own town points
 *    (`RegionHubTown.point`), not a parse of the region polygon's own SVG path commands; for a
 *    region shaped like a real maakunta the two are effectively the same box, and this avoids
 *    hand-rolling an SVG path bounding-box parser for a cosmetic crop.
 *  - The lede sentence states the largest town's share but doesn't name the specific "next few"
 *    towns (mockup: "loput jakautuvat kehyskuntiin Pirkkalasta Nokiaan...") - that clause isn't
 *    mechanically derivable from the data contract without an editorial judgment call.
 */
export const RegionHeader: React.FC<RegionHeaderProps> = ({ regionHubData, locale, className = '' }) => {
  const t = STRINGS[locale];
  const { towns, topRoles, neighbours, salary } = regionHubData;
  const defaultTown = towns[0] ?? null;
  const [selectedId, setSelectedId] = React.useState<string | null>(defaultTown?.placeId ?? null);
  const [hoverId, setHoverId] = React.useState<string | null>(null);
  const activeId = hoverId ?? selectedId;
  const activeTown = towns.find((town) => town.placeId === activeId) ?? defaultTown;

  const h1 = buildRegionHeaderH1(regionHubData.placeId, regionHubData.name, locale);
  const count = formatCount(regionHubData.count, locale);
  const locationsIndexHref = `/${locale}/${LOCALIZED_PATHS[locale].locations}`;
  const townCount = towns.length;
  const largestTownName = defaultTown?.name ?? regionHubData.name;
  const largestShare = defaultTown && regionHubData.count > 0
    ? Math.round((defaultTown.count / regionHubData.count) * 100)
    : 0;

  const maxTownCount = towns.reduce((max, town) => Math.max(max, town.count), 0) || 1;
  const viewBox = regionMapViewBox(towns);

  const employersLink = defaultTown ? buildEmployersInCityLink(locale, defaultTown.name) : null;

  function selectTown(placeId: string) {
    setSelectedId(placeId);
  }

  return (
    <section className={`region-info-header flex flex-col gap-8 sm:gap-10 ${className}`} aria-labelledby="region-header-title">
      <header className="flex flex-col gap-4">
        <nav aria-label={t.breadcrumbLocations} className="flex items-center gap-1.5 text-sm text-[#6f6858] dark:text-[#a89a80]">
          <a href={`/${locale}`} className="text-[#6f6858] dark:text-[#a89a80] hover:underline">{t.breadcrumbHome}</a>
          <span aria-hidden="true">/</span>
          <a href={locationsIndexHref} className="text-[#6f6858] dark:text-[#a89a80] hover:underline">{t.breadcrumbLocations}</a>
          <span aria-hidden="true">/</span>
          <span className="text-[#16150f] dark:text-[#F2EADF]">{regionHubData.name}</span>
        </nav>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6">
          <div className="flex flex-col gap-2 max-w-3xl">
            <h1
              id="region-header-title"
              className="m-0 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-[#16150f] dark:text-[#F2EADF]"
            >
              {h1}
            </h1>
            {regionHubData.count > 0 && defaultTown ? (
              <p className="m-0 text-sm sm:text-base leading-relaxed text-[#4a4438] dark:text-[#C4A882]">
                {t.lede(count, townCount, largestTownName, largestShare)}
              </p>
            ) : null}
          </div>
          <a
            href={locationsIndexHref}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full text-sm font-semibold whitespace-nowrap bg-white dark:bg-[#1F1509]/80 text-[#16150f] dark:text-[#F2EADF] border border-[#ddd5c8] dark:border-[#4A3520] hover:bg-[#F2EADF] dark:hover:bg-[#2A1D0F] transition-colors"
          >
            {t.backToFinland}
          </a>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: t.statOpenNow, value: count },
            { label: t.statNewInWeek, value: formatCount(regionHubData.weekCount, locale) },
            { label: t.statNoFinnish, value: formatPct(regionHubData.noFinnishPct, locale) },
            { label: t.statFlex, value: formatPct(regionHubData.flexPct, locale) },
            {
              label: salary ? t.salaryFor(salary.cityName) : t.statSalary,
              value: salary ? formatSalaryRange(salary.min, salary.max, locale) : '–',
            },
          ].map((tile) => (
            <div
              key={tile.label}
              className="flex flex-col gap-0.5 px-4 py-3 rounded-2xl bg-white dark:bg-[#1F1509]/80 border border-[#16150f]/[0.08] dark:border-[#4A3520]"
            >
              <span className="text-xs text-[#6f6858] dark:text-[#a89a80]">{tile.label}</span>
              <span className="font-mono-display text-xl font-bold tabular-nums text-[#16150f] dark:text-[#F2EADF]">{tile.value}</span>
            </div>
          ))}
        </div>
      </header>

      {towns.length > 0 ? (
        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start">
          <div className="flex flex-col gap-4 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-6 items-start">
              <div className="flex flex-col gap-2.5">
                <svg
                  viewBox={viewBox}
                  role="img"
                  aria-label={regionHubData.name}
                  className="w-full h-auto rounded-2xl bg-white dark:bg-[#1F1509]/40 border border-[#16150f]/[0.08] dark:border-[#4A3520]"
                >
                  {FINLAND_REGIONS.map((region) => (
                    <path
                      key={region.code}
                      d={region.d}
                      fill={region.code === regionHubData.mapCode ? '#a8c9c8' : '#ddd5c8'}
                      fillOpacity={region.code === regionHubData.mapCode ? 0.5 : 0.35}
                      stroke={region.code === regionHubData.mapCode ? '#4a7b79' : 'none'}
                    />
                  ))}
                  {towns.map((town, index) => {
                    if (!town.point) return null;
                    const [cx, cy] = town.point;
                    const r = Math.max(1.6, Math.sqrt(town.count / maxTownCount) * 13);
                    const bucket = Math.min(3, Math.floor((town.count / maxTownCount) * 4));
                    const isActive = town.placeId === activeId;
                    const fill = isActive ? ACTIVE_FILL : TEAL_RAMP[bucket];
                    const circle = (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill={fill}
                        stroke={isActive ? '#16150f' : '#f8f5f0'}
                        strokeWidth={0.6}
                        onMouseEnter={() => setHoverId(town.placeId)}
                        onMouseLeave={() => setHoverId(null)}
                        onClick={(e) => {
                          e.preventDefault();
                          selectTown(town.placeId);
                        }}
                      >
                        <title>{`${town.name}: ${formatCount(town.count, locale)}`}</title>
                      </circle>
                    );
                    return (
                      <g key={town.placeId}>
                        {town.linked ? <a href={town.href}>{circle}</a> : circle}
                        {index < TOP_LABELLED_TOWNS ? (
                          <text x={cx + r + 2} y={cy + 3} fontSize={4.2} fill="#1a2e2e" className="pointer-events-none select-none">
                            {town.name}
                          </text>
                        ) : null}
                      </g>
                    );
                  })}
                </svg>
                <div className="flex justify-between gap-2 text-xs text-[#6f6858] dark:text-[#a89a80]">
                  <span>{t.mapHint}</span>
                  <span>{t.mapAttribution}</span>
                </div>
              </div>
              <div className="flex flex-col min-w-0">
                <div className="grid grid-cols-[22px_minmax(0,130px)_1fr_56px] gap-2.5 pb-1.5 text-[11px] font-bold tracking-wide uppercase text-[#6f6858] dark:text-[#a89a80] border-b border-[#ddd5c8] dark:border-[#4A3520]">
                  <span>{t.tableRank}</span>
                  <span>{t.tableTown}</span>
                  <span>{t.tableShare}</span>
                  <span className="text-right">{t.tableOpen}</span>
                </div>
                {towns.map((town, index) => {
                  const isActive = town.placeId === activeId;
                  const barWidth = Math.max(1, Math.round((town.count / maxTownCount) * 100));
                  const rowClassName = `grid grid-cols-[22px_minmax(0,130px)_1fr_56px] items-center gap-2.5 px-1.5 py-1 -mx-1.5 rounded-lg ${
                    isActive ? 'bg-[#e9f1f0]' : ''
                  }`;
                  const rowContent = (
                    <>
                      <span className="text-xs tabular-nums text-[#6f6858] dark:text-[#a89a80]">{index + 1}</span>
                      <span className={`text-sm truncate ${isActive ? 'font-bold' : 'font-medium'} ${town.linked ? '' : 'text-[#6f6858] dark:text-[#a89a80]'}`}>
                        {town.name}
                      </span>
                      <span className="block h-2 rounded overflow-hidden bg-[#e9f1f0] dark:bg-[#1A2E22]">
                        <span
                          className="block h-full"
                          style={{ width: `${barWidth}%`, background: town.count === 0 ? '#ddd5c8' : TEAL_RAMP[Math.min(3, Math.floor((town.count / maxTownCount) * 4))] }}
                        />
                      </span>
                      <span className="font-mono-display text-sm font-bold text-right tabular-nums text-[#16150f] dark:text-[#F2EADF]">
                        {formatCount(town.count, locale)}
                      </span>
                    </>
                  );
                  return town.linked ? (
                    <a
                      key={town.placeId}
                      href={town.href}
                      onMouseEnter={() => setHoverId(town.placeId)}
                      onMouseLeave={() => setHoverId(null)}
                      onClick={(e) => {
                        e.preventDefault();
                        selectTown(town.placeId);
                      }}
                      className={`${rowClassName} no-underline text-[#16150f] dark:text-[#F2EADF]`}
                    >
                      {rowContent}
                    </a>
                  ) : (
                    <div
                      key={town.placeId}
                      onMouseEnter={() => setHoverId(town.placeId)}
                      onMouseLeave={() => setHoverId(null)}
                      className={rowClassName}
                    >
                      {rowContent}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {activeTown ? (
            <aside
              aria-live="polite"
              className="flex flex-col gap-3 p-5 sm:p-6 rounded-3xl bg-white dark:bg-[#1F1509]/80 border border-[#16150f]/[0.08] dark:border-[#4A3520] shadow-sm"
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold tracking-wide uppercase text-[#4a7b79]">{t.panelKicker(regionHubData.name)}</span>
                  <span className="font-mono-display text-2xl sm:text-3xl font-bold leading-tight text-[#16150f] dark:text-[#F2EADF]">{activeTown.name}</span>
                </div>
                <span className="text-xs text-right max-w-[150px] text-[#6f6858] dark:text-[#a89a80]">
                  {t.panelRankOf(towns.findIndex((tw) => tw.placeId === activeTown.placeId) + 1, towns.length)}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono-display text-4xl sm:text-5xl font-bold leading-none tabular-nums text-[#16150f] dark:text-[#F2EADF]">
                  {formatCount(activeTown.count, locale)}
                </span>
                <span className="text-sm text-[#6f6858] dark:text-[#a89a80]">{t.panelHeadlineLabel}</span>
              </div>
              {(() => {
                const share = regionHubData.count > 0 ? Math.round((activeTown.count / regionHubData.count) * 100) : 0;
                return (
                  <div className="flex flex-col gap-1.5 py-2.5 border-t border-[#16150f]/[0.08] dark:border-[#4A3520]">
                    <div className="flex justify-between gap-2 text-[13px]">
                      <span className="text-[#6f6858] dark:text-[#a89a80]">{t.panelShareLabel}</span>
                      <span className="font-bold tabular-nums text-[#16150f] dark:text-[#F2EADF]">{share}%</span>
                    </div>
                    <span className="block h-1.5 rounded overflow-hidden bg-[#e9f1f0] dark:bg-[#1A2E22]">
                      <span className="block h-full bg-[#4a7b79]" style={{ width: `${share}%` }} />
                    </span>
                  </div>
                );
              })()}
              {topRoles.length > 0 ? (
                <div className="flex flex-col gap-2 pt-2.5 border-t border-[#16150f]/[0.08] dark:border-[#4A3520]">
                  <span className="text-xs font-bold tracking-wide uppercase text-[#6f6858] dark:text-[#a89a80]">{t.panelRolesHeading}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {topRoles.map((role) => {
                      const roleDef = findRoleForNormalisedTitle(role.title, locale);
                      const href = roleDef
                        ? `/${locale}${buildLocationSrpPath(activeTown.slug, locale, undefined, roleDef.slug)}`
                        : `/${locale}${buildLocationSrpPath(activeTown.slug, locale)}?normalisedTitle=${encodeURIComponent(role.title)}`;
                      return (
                        <a
                          key={role.title}
                          href={href}
                          className="inline-flex items-center min-h-[34px] px-3 rounded-full text-[13px] font-semibold no-underline bg-[#e9f1f0] text-[#1a2e2e] border border-[#a8c9c8]"
                        >
                          {role.title}
                        </a>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <a
                href={activeTown.href}
                className="inline-flex items-center justify-center gap-2 min-h-[46px] px-4 rounded-full bg-[#3b5e45] text-white text-[15px] font-bold no-underline hover:opacity-90 transition-opacity"
              >
                {t.panelCta(formatCount(activeTown.count, locale), activeTown.name)}
              </a>
            </aside>
          ) : null}
        </section>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12 items-start">
        {topRoles.length > 0 ? (
          <div className="flex flex-col gap-3">
            <h2 className="m-0 font-mono-display text-lg sm:text-xl font-bold text-[#16150f] dark:text-[#F2EADF]">
              {t.topRolesHeading(regionHubData.name)}
            </h2>
            <div className="flex flex-wrap gap-2">
              {topRoles.map((role) => (
                <a
                  key={role.title}
                  href={role.href}
                  className="inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full text-sm font-semibold no-underline bg-[#e9f1f0] text-[#1a2e2e] border border-[#a8c9c8]"
                >
                  {role.title}
                  <span className="font-medium opacity-75 tabular-nums">{formatCount(role.count, locale)}</span>
                </a>
              ))}
            </div>
          </div>
        ) : null}
        {neighbours.length > 0 ? (
          <div className="flex flex-col gap-3">
            <h2 className="m-0 font-mono-display text-lg sm:text-xl font-bold text-[#16150f] dark:text-[#F2EADF]">{t.neighboursHeading}</h2>
            <div className="flex flex-wrap gap-2">
              {neighbours.map((neighbour) => (
                <a
                  key={neighbour.placeId}
                  href={neighbour.href}
                  className="inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full text-sm font-semibold no-underline bg-white dark:bg-[#1F1509]/80 text-[#16150f] dark:text-[#F2EADF] border border-[#ddd5c8] dark:border-[#4A3520]"
                >
                  {neighbour.name}
                  <span className="font-medium opacity-75 tabular-nums">{formatCount(neighbour.count, locale)}</span>
                </a>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-3 border-t border-[#ddd5c8] dark:border-[#4A3520]">
              <a
                href={hubIndexPath('roles', locale)}
                className="inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full text-sm font-semibold no-underline bg-white dark:bg-[#1F1509]/80 text-[#16150f] dark:text-[#F2EADF] border border-[#ddd5c8] dark:border-[#4A3520]"
              >
                {t.linkAllRoles}
              </a>
              {employersLink ? (
                <a
                  href={employersLink.href}
                  className="inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full text-sm font-semibold no-underline bg-white dark:bg-[#1F1509]/80 text-[#16150f] dark:text-[#F2EADF] border border-[#ddd5c8] dark:border-[#4A3520]"
                >
                  {t.linkEmployers(defaultTown?.name ?? regionHubData.name)}
                </a>
              ) : null}
              <a
                href={hubIndexPath('salary', locale)}
                className="inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full text-sm font-semibold no-underline bg-white dark:bg-[#1F1509]/80 text-[#16150f] dark:text-[#F2EADF] border border-[#ddd5c8] dark:border-[#4A3520]"
              >
                {t.linkSalary}
              </a>
            </div>
          </div>
        ) : null}
      </div>

      <p className="m-0 text-xs leading-relaxed text-[#6f6858] dark:text-[#a89a80]">{t.footnote(MIN_JOBS_FOOTNOTE)}</p>
    </section>
  );
};

export default RegionHeader;
